import type { RentalFilters, SortOption, TravelTimeFilter, RecentSearch } from './types'

/**
 * Build a human-readable display label from search parameters.
 * For text searches, returns the query text.
 * For filter searches, builds a comma-separated summary of active criteria.
 */
export function buildDisplayLabel(params: {
  type: 'text' | 'filters'
  query?: string
  filters?: RentalFilters
  tags?: string[]
  city?: string
  districts?: string[]
  travelTime?: TravelTimeFilter | null
}): string {
  // Text mode: use the query as-is
  if (params.type === 'text' && params.query?.trim()) {
    return params.query.trim()
  }

  // Filter mode: build summary from criteria
  const parts: string[] = []
  const fmt = (n: number) => n.toLocaleString('en-US')
  const f = params.filters

  if (f) {
    // Rooms
    const rMin = f.rooms_min
    const rMax = f.rooms_max
    if (rMin != null && rMax != null) {
      parts.push(rMin === rMax ? `${rMin} room${rMin !== 1 ? 's' : ''}` : `${rMin}\u2013${rMax} rooms`)
    } else if (rMin != null) {
      parts.push(`${rMin}+ rooms`)
    } else if (rMax != null) {
      parts.push(`up to ${rMax} rooms`)
    }

    // Price
    if (f.price_min != null && f.price_max != null) {
      parts.push(`${fmt(f.price_min)}\u2013${fmt(f.price_max)} CZK`)
    } else if (f.price_max != null) {
      parts.push(`max ${fmt(f.price_max)} CZK`)
    } else if (f.price_min != null) {
      parts.push(`from ${fmt(f.price_min)} CZK`)
    }

    // Area
    if (f.area_min != null && f.area_max != null) {
      parts.push(`${f.area_min}\u2013${f.area_max} m\u00B2`)
    } else if (f.area_min != null) {
      parts.push(`min ${f.area_min} m\u00B2`)
    } else if (f.area_max != null) {
      parts.push(`max ${f.area_max} m\u00B2`)
    }

    // Property type (only if not default)
    if (f.type && f.type !== 'apartment') {
      parts.push(f.type)
    }
  }

  // Location
  if (params.districts && params.districts.length > 0) {
    parts.push(params.districts.join(', '))
  } else if (params.city && params.city !== 'Praha') {
    parts.push(params.city)
  }

  // Tags (show first 3 keyword names)
  if (params.tags && params.tags.length > 0) {
    const shown = params.tags.slice(0, 3).map((t) => t.replace(/_/g, ' '))
    if (params.tags.length > 3) {
      shown.push(`+${params.tags.length - 3} more`)
    }
    parts.push(...shown)
  }

  // Travel time
  if (params.travelTime) {
    parts.push(`${params.travelTime.minutes} min from ${params.travelTime.origin}`)
  }

  return parts.length > 0 ? parts.join(', ') : 'All apartments'
}

/**
 * Create a RecentSearch entry from current search state.
 */
export function createRecentSearch(params: {
  type: 'text' | 'filters'
  query?: string
  filters?: RentalFilters
  tags?: string[]
  city?: string
  districts?: string[]
  travelTime?: TravelTimeFilter | null
  sort?: SortOption
}): RecentSearch {
  return {
    id: crypto.randomUUID(),
    type: params.type,
    query: params.query || undefined,
    filters: params.filters,
    tags: params.tags && params.tags.length > 0 ? params.tags : undefined,
    city: params.city,
    districts: params.districts && params.districts.length > 0 ? params.districts : undefined,
    travelTime: params.travelTime,
    sort: params.sort,
    displayLabel: buildDisplayLabel(params),
    searchedAt: new Date().toISOString(),
  }
}
